import styled from '@emotion/styled/macro'
import _ from 'lodash'
import { Checkbox, CheckboxProps, DialogTitle, InputLabel } from '@material-ui/core'
import { GrantCheckedMap, trans, TranslationGroup } from 'lib/types'
import {
  BORDER1_COLOR,
  GRANTS,
  getHumanGrantName,
  TEXTFIELD_LABEL_COLOR,
  USER_ASSIGNABLE_GRANTS,
  getAppColors,
} from 'lib/constants'
import { memo, useState } from 'react'
import { getCheckedStatus, getCheckboxColor, isDisabled } from './utils'
import { ActionButton, StyledDialogActions } from 'components/widgets'
import { Grants, PopUpNotifications } from 'models'
import { createOrUpdate } from 'models/Users'
import { User } from 'lib/types'
interface StyledCheckboxProps extends CheckboxProps {
  checkboxcolor: string
}

const StyledFormSectionTitle = styled(InputLabel)({
  color: TEXTFIELD_LABEL_COLOR,
  fontFamily: 'Montserrat',
  fontSize: '1.4rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.5rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  margin: '3.2rem 0 1.6rem',
})

const StyledMainWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
})

const StyledListContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '25px',
})

const StyledSeparator = styled.div({
  border: `1px solid ${BORDER1_COLOR}`,
  marginBottom: '14px',
})

const StyledRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
})

const StyledText = styled.span({
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 'normal',
  fontSize: '1.6rem',
})

export const StyledCheckbox = styled(Checkbox)<StyledCheckboxProps>(props => ({
  border: `1px solid ${getAppColors('--color-surface')}`,
  padding: '0',
  '&.Mui-checked': {
    color: props.checkboxcolor,
  },
}))

const StyledActionButton = styled(ActionButton)({
  width: '97.6px',
  height: '28px',
})

export const StyledDialogTitle = styled(DialogTitle)({
  fontFamily: 'Montserrat',
  padding: '1.6rem 0',
  h2: {
    fontFamily: 'Montserrat',
    fontSize: '1.8rem',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '2.9rem',
    letterSpacing: '0',
    textAlign: 'left',
  },
})

export interface AssignModalProps {
  userId?: string
  orgId: string
  close?: () => void
  title?: string
  setGrants?: Function
  firstName?: string | undefined
  lastName?: string | undefined
  user?: User
}

export const updateGrants = async (
  userId: string,
  orgId: string,
  oldGrantCheckMap: GrantCheckedMap,
  newGrantCheckMap: GrantCheckedMap,
  grantIdsByName: { [name: string]: string } = {},
) => {
  const wantsAdmin = !!newGrantCheckMap[GRANTS.ADMIN]
  // Do not set or delete `ASSOCIATE` grant here.
  delete oldGrantCheckMap[GRANTS.ASSOCIATE]
  delete newGrantCheckMap[GRANTS.ASSOCIATE]
  // The `DISPATCHER` grant is just a combination of `PORTAL_USER` and `RESPONDER`, not an actual grant.
  delete newGrantCheckMap[GRANTS.DISPATCHER]
  if (wantsAdmin) {
    delete newGrantCheckMap[GRANTS.INSTALLER]
    delete newGrantCheckMap[GRANTS.PORTAL_USER]
    delete newGrantCheckMap[GRANTS.RESPONDER]
  }

  // Promoting to admin is the entire change: the server prunes the narrower
  // grants itself once an admin grant lands.
  if (wantsAdmin && !oldGrantCheckMap[GRANTS.ADMIN]) {
    await updateGrant(oldGrantCheckMap, true, GRANTS.ADMIN, userId, orgId)
    return
  }

  const changed = Object.keys(newGrantCheckMap).filter(name => !!oldGrantCheckMap[name] !== !!newGrantCheckMap[name])
  const toAdd = changed.filter(name => newGrantCheckMap[name])
  const toRemove = changed.filter(name => !newGrantCheckMap[name])

  // Rename in place wherever a removal can pay for an addition. PUT
  // /grants/:id neither trips the create-side guard that rejects every grant
  // while an admin grant is present, nor runs the delete cascade that strips the
  // org's alert targets and licenses — so a role swap keeps the user's SMS and
  // email subscriptions and never leaves them grantless mid-change.
  while (toRemove.length && toAdd.length) {
    const from = toRemove[0]
    const grantId = grantIdsByName[from]
    if (!grantId) break

    toRemove.shift()
    await Grants.renameGrant(grantId, toAdd.shift()!, orgId)
  }

  // Whatever is left is a real addition or a real removal. Sequential, not a
  // `forEach` of un-awaited promises: a delete cascades server-side into
  // re-upserting the user's single responder row, and two of those in flight at
  // once race on that row, with the loser raising instead of returning an error.
  for (const name of toAdd) {
    await updateGrant(oldGrantCheckMap, true, name, userId, orgId)
  }

  for (const name of toRemove) {
    await updateGrant(oldGrantCheckMap, false, name, userId, orgId)
  }
}

async function updateGrant(
  oldGrantCheckMap: GrantCheckedMap,
  checked: boolean,
  name: string,
  userId: string,
  orgId: string,
) {
  if (!!oldGrantCheckMap[name] !== checked) {
    return checked ? await Grants.createGrant(name, userId, orgId) : Grants.deleteGrant(name, userId, orgId)
  }
}

export const AssignModal = memo(
  ({ user, userId, orgId, close, title, setGrants, firstName, lastName }: AssignModalProps) => {
    const common: TranslationGroup = trans.common()

    const usersGrants = Grants.useUserOrgGrants(userId || '', orgId)
    const [oldGrantCheckMap] = useState((): GrantCheckedMap => {
      const out: GrantCheckedMap = {}
      usersGrants.forEach(grant => (out[grant.attributes.name] = !!grant))
      return out
    })
    // Captured from the same snapshot as `oldGrantCheckMap`, so a rename targets
    // the grant row the checkboxes were built from.
    const [grantIdsByName] = useState((): { [name: string]: string } => {
      const out: { [name: string]: string } = {}
      usersGrants.forEach(grant => (out[grant.attributes.name] = grant.id))
      return out
    })
    const [newGrantsMap, setGrantsByName] = useState(_.clone(oldGrantCheckMap))
    const [applying, setApplying] = useState(false)

    const nameChanged = firstName !== user?.attributes?.firstName || lastName !== user?.attributes?.lastName

    // Grants first, then the name — and one at a time. Both writes end in the
    // same server-side responder upsert, so overlapping them races on the
    // responder row. Sequencing grants first also means that when a removed
    // grant takes the org's alert targets with it, the later user update finds
    // no targets left and skips the upsert branch that can raise.
    const handleApply = async () => {
      if (applying) return
      setApplying(true)
      try {
        await updateGrants(userId!, orgId, oldGrantCheckMap, newGrantsMap, grantIdsByName)
        if (nameChanged) await createOrUpdate({ ...user, firstName, lastName })
        close!()
      } catch (error: any) {
        PopUpNotifications.fireErrorObject(error)
      } finally {
        setApplying(false)
      }
    }

    return (
      <>
        {title && <StyledFormSectionTitle>{title.toUpperCase()}</StyledFormSectionTitle>}
        <StyledMainWrapper>
          <StyledSeparator />
          <StyledListContainer>
            <GrantsList grants={newGrantsMap} />
          </StyledListContainer>
          <StyledSeparator />
          {userId && close && (
            <StyledDialogActions>
              <StyledActionButton onClick={() => close()}>{common.Cancel}</StyledActionButton>
              <StyledActionButton onClick={handleApply}>{common.Apply}</StyledActionButton>
            </StyledDialogActions>
          )}
        </StyledMainWrapper>
      </>
    )

    function GrantsList({ grants }: { grants: GrantCheckedMap }) {
      return (
        <>
          {USER_ASSIGNABLE_GRANTS.map(grantName => (
            <StyledRow key={grantName}>
              <StyledText>{getHumanGrantName(grantName)}</StyledText>
              <StyledCheckbox
                checkboxcolor={getCheckboxColor(grants, grantName)}
                checked={getCheckedStatus(grants, grantName)}
                onChange={e => setGrant(grants, grantName, e.target.checked)}
                disabled={isDisabled(grants, grantName)}
              />
            </StyledRow>
          ))}
        </>
      )
    }

    function setGrant(grants: GrantCheckedMap, grantName: string, checked: boolean) {
      switch (grantName) {
        case GRANTS.ADMIN:
          for (const key in grants) {
            if (key !== GRANTS.ADMIN) {
              grants[key] = false
            }
          }
          break
        case GRANTS.DISPATCHER:
          grants[GRANTS.PORTAL_USER] = checked
          grants[GRANTS.RESPONDER] = checked
          break
        default:
          break
      }
      setGrantsByName({ ...grants, [grantName]: checked })
      if (setGrants) setGrants({ ...grants, [grantName]: checked })
    }
  },
)
AssignModal.displayName = 'AssignModal'
